import { Address, ProjectParams } from '@/types/api/organisations.types';
import { ProjectUserRole, Role } from '@/types/api/project.types';
import { UserOrganisation } from '@/types/api/auth.types';
import { BoqPosition } from '@/types/api/boqs.types';
import { ReturnListItem } from '@/types/api/returns.types';
import type { VariationScopeFields } from '@/types/api/variation-scope.types';

export type BasicInfo = {
  name: string;
  siteShortCode: string;
  erfNumber?: string;
  projectId: string;
  startDate: string;
  endDate: string;
};

export type ClientInfo = {
  clientName?: string;
  email?: string;
  contactNumber?: string;
  contactNumberCountryCode?: string;
  companyVat?: string;
  companyName?: string;
  clientType: string;
};

export type BankFundingInfo = {
  bankId: number;
  amount: number;
  accountNumber: string;
  additionalDetails?: string;
};

export type SiteUser = {
  email: string;
  siteRoleCode: string;
};

export type CreateSiteBody = {
  basicInfo: BasicInfo;
  clientInfo: ClientInfo;
  fundingInfo: {
    bankFundingInfo?: BankFundingInfo;
    selfFundingInfo?: {
      amount: number;
    };
  };
  additionalInfo: {
    address?: Address | null;
    deliveryAddress?: Address | null;
  };
  siteUsers: SiteUser[];
  contractType: string;
  targetMargin: number | null;
  defaultMarkup?: number;
};

export type SiteUserRole = ProjectUserRole & {
  id: string;
};

export type Bank = {
  id: number;
  name: string;
};

export type Funding = {
  id: string;
  siteId: string;
  type: string;
  amount: number;
  accountNumber: string;
  additionalDetails: string;
  bank: Bank;
};

export type SiteBudget = {
  available: number;
  used: number;
  siteFundingBudget: number;
};

export enum ContractTypeEnum {
  noContract = 'No_Contract',
  fixedCost = 'Fixed_Cost',
  costPlus = 'Cost_Plus',
}

export type ContractType = {
  value: string;
  displayValue: string;
};

export type Site = {
  id: string;
  name: string;
  siteShortCode: string;
  erfNumber?: string;
  projectId: string;
  archived: boolean;
  address: Address;
  deliveryAddress: Address;
  clientType: string;
  clientName: string;
  clientEmail: string;
  clientContactNumber: string;
  clientContactNumberCountryCode: string;
  companyName: string;
  companyVAT: string;
  siteStatus: Role;
  siteUserRoles: SiteUserRole[] | null;
  fundings: Funding[];
  risk: boolean;
  showCashflowPrompt: boolean;
  contractType: ContractTypeEnum;
  targetMargin: number;
  defaultMarkup: number;
  budget: SiteBudget;
  accountId: string;
  startDate: string;
  endDate: string;
  closedAt?: string;
};

export type SiteUserResponse = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organisations: UserOrganisation[];
  emailConfirmed: boolean;
};

export enum ClientType {
  company = 'Company',
  private = 'Private',
  none = 'None',
}

export type SiteParams = ProjectParams & {
  name?: string;
};

export type AdditionalUpdateSiteFields = {
  name: string;
  siteShortCode: string;
  erfNumber?: string;
  startDate: string;
  endDate: string;
  siteStatusId?: number;
  archived: boolean;
  targetMargin: number | null;
  defaultMarkup?: number;
  overrideItemMarkups: boolean;
  /** Sent only when a date moved: the site's activity log keeps it beside the date change. */
  dateChangeReason?: string;
};

export type UpdateSiteBody = Omit<CreateSiteBody, 'basicInfo' | 'siteUsers'> &
  AdditionalUpdateSiteFields;

export type BOQBudget = {
  approvedVariationTotal: number;
  sellingApprovedVariationTotal: number;
  sellingSpent: number;
  sellingTotal: number;
  sellingVariationTotal: number;
  spent: number;
  total: number;
  variationTotal: number;
  clientVariationCostTotal: number;
  clientVariationSellingTotal: number;
};

export type BOQSection = {
  id: string;
  position: number;
  path: number[];
  name: string;
  budget: number;
  budgetPercent: number;
  budgetPercentThreshold: number;
  sections: BOQSection[];
  itemGroups: BOQItemGroup[];
  items: BOQItem[];
  sellingBudget: number;
  sellingBudgetPercent: number;
  clientVariationCostPrice: number;
  clientVariationSellingPrice: number;
  fromClientVariation: boolean;
};

export type PendingVariation = {
  quantity: number;
  rate: number;
  total: number;
  specification: string;
  vendorId: string;
  vendorItemId: string;
  vendorName: string;
  /**
   * Whether the item this variation would put on the line has itself been deleted. The line's own
   * `vendorItemUnavailable` cannot catch this: PLE-1171 flags the lines pointing at a dead item at
   * deletion time, and this line is not pointing at it yet — approving would move it onto an item
   * nothing will ever flag (PLE-1435).
   */
  vendorItemUnavailable?: boolean;
};

export type CreatedDelivery = {
  at: string;
  byId: string;
  byName: string;
};

export type SellingInfo = {
  markup: number | null;
  rate: number | null;
  total: number | null;
};

export type Delivery = {
  created: CreatedDelivery;
  deliveryId: string;
  quantity: number;
  rate: number;
  returnId: string;
  sellingInformation: SellingInfo;
  total: number;
};

export type ItemHistoryReturn = {
  returnId: string;
  returnNumber: string;
  returnItemId: string;
  type: string;
  markup: number | null;
  quantity: number;
  rate: number;
  sellingRate: number | null;
  total: number;
  sellingTotal: number | null;
  createdAt: string;
  createdById: string;
  createdByName: string;
  approvedAt: string;
  approvedById: string;
  approvedByName: string;
  deliveryId: string;
  reversal: boolean;
};

export type ItemHistoryContraCharge = {
  contraId: string;
  orderId: string;
  orderNumber: string;
  quantity: number;
  rate: number;
  total: number;
  approvedAt: string;
  approvedBy: string;
  approvedByName: string;
  createdAt: string;
  createdBy: string;
  createdByName: string;
};

export type Order = {
  orderNumber: string;
  orderId: string;
  quantity: number;
  unit?: string;
  rate: number;
  total: number;
  approvedAt: string;
  approvedBy?: string;
  approvedByName: string;
  itemId: string;
  itemName: string;
  vendorId: string;
  vendorName: string;
  createdAt: string;
  createdByName: string;
  contraId?: string;
  lineItemId?: string;
  name?: string;
  parentSection?: string;
  path?: number[];
  specification?: string;
  sellingMarkup: number;
  sellingRate: number;
  sellingTotal: number;
  returnId?: string;
  deliveries: Delivery[];
  type?: string;
  returns: ItemHistoryReturn[];
};

export type MergedOrderReturn = Order & ReturnListItem;

/**
 * How a served BOQ line stands against the provisional budget it draws on, derived on read and
 * never stored (PE-1278). A declined allocation's line is removed, so there is no `Declined`.
 * `Reversed` is written only from M4, but is on the wire now so this union is whole.
 */
export type ProvisionalBudgetLineState = 'Pending' | 'Allocated' | 'Reversed';

/**
 * The provisional-budget block on a served BOQ line (PE-1278's `ProvisionalBudgetLineVM`).
 *
 * Context: it lives here rather than in the provisional-budgets slice because it is the BOQ read's
 * shape, and the slice already imports from this file, so declaring it there would make a cycle.
 * The slice re-exports it.
 *
 * Every optional field is omitted when it does not apply, never sent as `null` (DL-136): check
 * presence, never `=== null`.
 */
export type ProvisionalBudgetLine = {
  provisionalBudgetId: string;
  state: ProvisionalBudgetLineState;
  allocationId: string;
  /**
   * The allocation request awaiting a decision. Present only while `state` is `Pending`.
   */
  requestId?: string;
  /**
   * The proposed quantity. Present only while `state` is `Pending`.
   *
   * Context: a pending line is written worth nothing on the bill, and the approval writes its
   * figures, so while it waits its quantity, rate and amount ride on the allocation and arrive here
   * (DL-274). Read the line's own figures once it is `Allocated`.
   */
  quantity?: number;
  /**
   * The proposed price per unit. Present only while `state` is `Pending` (DL-274).
   */
  rate?: number;
  /**
   * The server's amount for the proposed quantity and rate. Present only while `state` is
   * `Pending` (DL-274). Server-computed: render it, never derive it.
   */
  amount?: number;
};

export type BOQItem = {
  id: string;
  orderId?: string;
  position: number;
  path: number[];
  specification: string;
  budgetPercent: number;
  name: string;
  vendorId: string;
  vendorItemId: string;
  vendorName: string;
  /**
   * The line's catalogue link no longer resolves to a live vendor or item — written at deletion
   * time and persisted, never recomputed on read (PLE-1171). Mirrors `vendorItemUnavailable` on
   * {@link BoqLineProjection}; the vendor *name* snapshot stays on the line, so it is still
   * labelled rather than blanked.
   */
  vendorItemUnavailable: boolean;
  type: string;
  unitType: string;
  rate: number;
  reference: string;
  quantity: number;
  totalAmount: number;
  remaining: number;
  spent: number;
  drawNumber: string;
  startDate: string;
  endDate: string;
  hasPendingVariation: boolean;
  pendingVariation: PendingVariation;
  orders: Order[];
  description?: string;
  sectionName: string | null;
  sectionId?: string;
  subSectionName: string | null;
  groupName: string | null;
  defaultRate?: number;
  defaultQuantity?: number;
  total?: number;
  totalInclVat?: number;
  vatRegistered?: boolean;
  newItem?: boolean;
  orderVariation?: boolean;
  variationQuantity?: number;
  variationRate?: number;
  variationTotal?: number;
  desiredQuantity: number | null;
  newVendorId?: string;
  boqItemInGroup?: boolean;
  boqItemPath?: number[];
  itemVariation: number;
  variationTriggered?: boolean;
  specVariationName?: string | null;
  specVariationId?: string | null;
  qtyVariation?: number | null;
  rateVariation?: number | null;
  typeVariation?: string | null;
  unitTypeVariation?: string | null;
  vendorVariation?: string | null;
  nameVariation?: string | null;
  refVariation?: string | null;
  newItemVariation?: boolean;
  markup: number;
  sellingAmount: number;
  sellingRate: number;
  sellingSpent: number;
  isGroupItem?: boolean;
  contraCharges: ItemHistoryContraCharge[];
  newItemVariationCreated?: boolean;
  inputKey?: number;
  /**
   * Omitted on a line that draws on no provisional budget, and for a reader who may not see
   * provisional budgets (DL-136, DL-59).
   */
  provisionalBudget?: ProvisionalBudgetLine;
};

export type BOQItemGroup = {
  id: string;
  position: number;
  path: number[];
  name: string;
  description: string;
  unitType: string;
  unitAmount: number;
  totalAmount: number;
  spent: number;
  items: BOQItem[];
  sellingAmount: number;
  sellingSpent: number;
};

export type Variation = VariationScopeFields & {
  id: string;
  lineItemId: string;
  sectionId: string;
  itemName: string;
  itemSpecification: string;
  oldItemSpecification: string;
  newItemSpecification: string;
  itemReference: string;
  vendorName: string;
  oldVendorName: string;
  newVendorName: string;
  vendorItemId: string;
  oldVendorItemId: string;
  newVendorItemId: string;
  type: string;
  unitType: string;
  oldQuantity: number;
  newQuantity: number;
  oldRate: number;
  newRate: number;
  status: string;
  createdBy?: string;
  createdAt: string;
  createdByName?: string;
  reviewedBy?: string;
  reviewedAt: string;
  reviewedByName?: string;
  path?: BoqPosition;
  inGroup: boolean;
};

export type BoqBody = {
  templateId: string;
  useTemplate: boolean;
};

export type BOQ = {
  id: string;
  siteId: string;
  projectId: string;
  organisationId: string;
  status: BOQStatus;
  budget: BOQBudget;
  contractType: ContractTypeEnum;
  defaultMarkup: number;
  contraCharges: Order[];
  sections: BOQSection[];
  variations: Variation[];
  // Context: the API stamps the request fields on Draft → Pending and never clears any of these,
  // so bills from before the stamps carry nulls (BoqVM).
  requestedBy?: string | null;
  requestedByName?: string | null;
  requestedAt?: string | null;
  reviewNote?: string | null;
  reviewedBy?: string | null;
  reviewedByName?: string | null;
  reviewedAt?: string | null;
};

export enum BOQStatus {
  draft = 'Draft',
  pending = 'Pending',
  approved = 'Approved',
}

export type ItemToGroup = {
  position: number;
  path: number[];
  isGroupOrGroupItem: boolean;
};

export type Draw = {
  id: string;
  name: string;
  position: number;
  submittedDate: string;
  projectedAmount: number;
  receivedDate: string;
  actualAmount: number;
  siteId: string;
  date: string;
  period: string;
  claimStatus: string;
};

export type ClaimSummary = {
  previousTotal: number;
  currentTotal: number;
  balance: number;
};

export type ClaimSection = {
  sectionId: string;
  name: string;
  path: number[];
  budgetPercentThreshold: number;
  valuationPercent: number;
  valuationAmount: number;
  previousPercent: number;
  remainingPercent: number;
  currentPercent: number;
  currentAmount: number;
  sections: ClaimSection[];
};

export type Claim = {
  id: string;
  drawId: string;
  siteId: string;
  projectId: string;
  organisationId: string;
  date: string;
  status: string;
  summary: ClaimSummary;
  sections: ClaimSection[];
  createdBy: string;
  createdAt: string;
  lastModifiedAt: string;
  deletedAt: string;
};

export type UpdateClaim = {
  path: number[];
  currentPercent: number;
};

export type CompleteClaim = {
  status: string;
};

export enum ClaimStatuses {
  UNINITIATED = 'Uninitiated',
  IN_PROGRESS = 'InProgress',
  SUBMITTED = 'Submitted',
}

export type UpdateCashflow = {
  month: string;
  overheads: number;
};

export type ErfNumberValidationParams = {
  projectId: string;
  erfNumber?: string;
  excludeSiteId?: string;
};

/** One site an order-driven approval or placement would move, as the server judged it. */
export type SiteDateMove = {
  siteId: string;
  siteName: string;
  currentStart: string;
  currentEnd: string;
  resultingStart: string;
  resultingEnd: string;
};

/**
 * The details of a `400` of kind `SiteDateReasonRequired`: the sites whose dates the refused
 * request would have moved without a reason. The refused request wrote nothing.
 */
export type SiteDateReasonRequired = {
  sites: SiteDateMove[];
};
